import { forwardRef } from 'react';
import clsx from 'clsx';

import { ButtonWrapper } from '../ButtonWrapper';
import { resolveVariantClasses } from '../utils';
import type { ButtonVariant } from '../types';

export type IconButtonSize =
  | 'xxs'
  | 'xs'
  | 'sm'
  | 'base'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl';

type IBBaseProps = {
  variant?: ButtonVariant;
  size?: IconButtonSize;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  container?: boolean;
  rounded?: boolean;
  className?: string;
};

type AProps = IBBaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement>;
type BProps = IBBaseProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export type IconButtonProps = AProps | BProps;

export const IconButton = forwardRef(function IconButton(
  {
    className,
    children,
    size = 'base',
    variant = 'primary',
    container = true,
    rounded = true,
    disabled,
    disabledReason,
    onClick,
    ...props
  }: IconButtonProps,
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const cssClass = clsx(
    'inline-flex justify-center items-center',
    rounded && 'rounded-full',
    container && resolveVariantClasses(variant),
    {
      ['w-3 h-3 text-[0.5rem] p-0.5']: size === 'xxs',
      ['w-4 h-4 text-[0.5rem] p-1']: size === 'xs',
      ['w-5 h-5 text-xxs p-1']: size === 'sm',
      ['w-6 h-6 text-xs p-1.5']: size === 'base',
      ['w-8 h-8 text-base p-2']: size === 'md',
      ['w-10 h-10 text-md p-2']: size === 'lg',
      ['w-14 h-14 text-[1.75rem] p-3']: size === 'xl',
      ['w-16 h-16 text-xl p-3']: size === '2xl',
      ['w-24 h-24 text-3xl p-4']: size === '3xl',
    },

    className
  );

  function handleClick(/* event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent> */) {
    if (disabled) {
      // TODO Should be removed before production
      console.warn('Button is disabled, click is ignored');
      return;
    }

    if (typeof onClick === 'function') {
      onClick();
    }
  }

  return (
    <ButtonWrapper
      className={cssClass}
      {...props}
      ref={ref}
      onClick={handleClick}
      title={
        props.title || (disabled && disabledReason ? disabledReason : undefined)
      }
      aria-disabled={disabled}
    >
      {children}
    </ButtonWrapper>
  );
});

export default IconButton;
